import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getErrorMessage, logError } from '../../../utils/errorHandler';
import styles from './Account.module.css';
import Toast from '../../Shared/Toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faUser, faLock, faKey, faCrown, faUsers, faCheck, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';

const DISPLAY_NAME_COOLDOWN_DAYS = 60;

const Account: React.FC = () => {
  const [displayName, setDisplayName] = useState<string>('');
  const [displayNameCooldownDaysLeft, setDisplayNameCooldownDaysLeft] = useState<number>(0);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [hasPassword, setHasPassword] = useState<boolean>(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const { currentTheme, setTheme, availableThemes } = useTheme();
  const { user } = useAuth();

  // Family Links state
  const [familyLinks, setFamilyLinks] = useState<any[]>([]);
  const [familySearch, setFamilySearch] = useState('');
  const [familyLoading, setFamilyLoading] = useState(false);

  const PREMIUM_ROLES = ['Owner', 'Admin', 'GameMaster', 'Premium'];
  const hasPremiumAccess = user?.role && PREMIUM_ROLES.includes(user.role.name);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userResponse = await axios.get('/api/auth/user', { withCredentials: true });
        setDisplayName(userResponse.data.displayName || '');
        setHasPassword(!!userResponse.data.password);
        if (userResponse.data.lastDisplayNameChangedAt) {
          const msSince = Date.now() - new Date(userResponse.data.lastDisplayNameChangedAt).getTime();
          const daysSince = msSince / (1000 * 60 * 60 * 24);
          const daysLeft = Math.max(0, Math.ceil(DISPLAY_NAME_COOLDOWN_DAYS - daysSince));
          setDisplayNameCooldownDaysLeft(daysLeft);
        }
      } catch (error) {
        logError(error, 'Account.fetchUserData');
        setToastMessage(getErrorMessage(error, 'Failed to load account data.'));
        setToastType('error');
      }
    };
    fetchUserData();
    fetchFamilyLinks();
  }, []);

  const fetchFamilyLinks = async () => {
    try {
      const res = await axios.get('/api/user/family-links', { withCredentials: true });
      setFamilyLinks(res.data.familyLinks || []);
    } catch (error) {
      logError(error, 'Account.fetchFamilyLinks');
    }
  };

  const sendFamilyLink = async () => {
    if (!familySearch.trim()) return;
    setFamilyLoading(true);
    try {
      await axios.post('/api/user/family-links', { targetUsername: familySearch.trim() }, { withCredentials: true });
      setToastMessage('Family link request sent!');
      setToastType('success');
      setFamilySearch('');
      await fetchFamilyLinks();
    } catch (error: any) {
      logError(error, 'Account.sendFamilyLink');
      setToastMessage(getErrorMessage(error, 'Failed to send family link request.'));
      setToastType('error');
    } finally {
      setFamilyLoading(false);
    }
  };

  const respondFamilyLink = async (linkId: number, action: 'accept' | 'reject') => {
    try {
      await axios.put(`/api/user/family-links/${linkId}`, { action }, { withCredentials: true });
      setToastMessage(action === 'accept' ? 'Family link confirmed!' : 'Family link rejected.');
      setToastType('success');
      await fetchFamilyLinks();
    } catch (error) {
      logError(error, 'Account.respondFamilyLink');
      setToastMessage(getErrorMessage(error, 'Failed to update family link.'));
      setToastType('error');
    }
  };

  const removeFamilyLink = async (linkId: number) => {
    try {
      await axios.delete(`/api/user/family-links/${linkId}`, { withCredentials: true });
      setToastMessage('Family link removed.');
      setToastType('success');
      await fetchFamilyLinks();
    } catch (error) {
      logError(error, 'Account.removeFamilyLink');
      setToastMessage(getErrorMessage(error, 'Failed to remove family link.'));
      setToastType('error');
    }
  };

  const handleUpdateDisplayName = async () => {
    try {
      await axios.post(
        '/api/auth/account/update-displayname',
        { displayName },
        { withCredentials: true }
      );
      setDisplayNameCooldownDaysLeft(DISPLAY_NAME_COOLDOWN_DAYS);
      setToastMessage('Display name updated successfully.');
      setToastType('success');
    } catch (error: any) {
      logError(error, 'Account.handleUpdateDisplayName');
      setToastMessage(getErrorMessage(error, 'Error updating display name. Please try again.'));
      setToastType('error');
    }
  };

  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) {
      setToastMessage('New password and confirmation do not match.');
      setToastType('error');
      return;
    }

    try {
      await axios.post(
        '/api/auth/account/update-password',
        {
          currentPassword: hasPassword ? currentPassword : null,
          newPassword,
        },
        { withCredentials: true }
      );
      setToastMessage('Password updated successfully.');
      setToastType('success');
    } catch (error: any) {
      logError(error, 'Account.handleUpdatePassword');
      setToastMessage(getErrorMessage(error, 'Error updating password. Please try again.'));
      setToastType('error');
    }
  };

  return (
    <div className={styles.accountContainer}>
      <h1>Account Settings</h1>

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      <div className={styles.settingsSection}>
        <h2><FontAwesomeIcon icon={faPalette} /> UI Theme</h2>
        <div className={styles.formGroup}>
          <label htmlFor="theme">Select Theme:</label>
          <div className={styles.themeSelectorWrapper}>
            <select
              id="theme"
              value={currentTheme.name}
              onChange={(e) => setTheme(e.target.value)}
              className={styles.themeSelector}
            >
              {availableThemes
                .filter((theme) => !theme.premium || hasPremiumAccess)
                .map((theme) => (
                  <option key={theme.name} value={theme.name}>
                    {theme.displayName}{theme.premium ? ' ✦' : ''}
                  </option>
                ))}
            </select>
          </div>
          <p className={styles.themeDescription}>{currentTheme.displayName}: {currentTheme.colors.text ? 'Visual style active' : 'Loading...'}</p>
          {!hasPremiumAccess && (
            <p className={styles.themeDescription} style={{ opacity: 0.7 }}>
              <FontAwesomeIcon icon={faCrown} /> <strong>Herald's Dawn</strong> is a Premium exclusive theme. <a href="/premium" style={{ color: 'inherit' }}>Upgrade to unlock it.</a>
            </p>
          )}
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h2><FontAwesomeIcon icon={faUser} /> Display Name</h2>
        {displayNameCooldownDaysLeft > 0 ? (
          <p className={styles.themeDescription}>
            You can change your display name again in <strong>{displayNameCooldownDaysLeft} day{displayNameCooldownDaysLeft !== 1 ? 's' : ''}</strong>.
          </p>
        ) : (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="displayName">New Name:</label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
              />
            </div>
            <p className={styles.themeDescription} style={{ opacity: 0.7 }}>
              Once set, your display name cannot be changed for {DISPLAY_NAME_COOLDOWN_DAYS} days.
            </p>
            <button className={styles.updateButton} onClick={handleUpdateDisplayName}>
              Update Name
            </button>
          </>
        )}
      </div>

      <div className={styles.settingsSection}>
        <h2><FontAwesomeIcon icon={hasPassword ? faLock : faKey} /> {hasPassword ? 'Update Password' : 'Set Password'}</h2>
        {hasPassword && (
          <div className={styles.formGroup}>
            <label htmlFor="currentPassword">Current Password:</label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
            />
          </div>
        )}
        <div className={styles.formGroup}>
          <label htmlFor="newPassword">New Password:</label>
          <input
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter your new password"
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="confirmPassword">Confirm New Password:</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
          />
        </div>
        <button className={styles.updateButton} onClick={handleUpdatePassword}>
          {hasPassword ? 'Update Password' : 'Set Password'}
        </button>
      </div>

      <div className={styles.settingsSection}>
        <h2><FontAwesomeIcon icon={faUsers} /> Family Links</h2>
        <p className={styles.themeDescription}>
          Link family accounts that share the same network. Linked accounts won't trigger shared-IP flags, but boosting between them is still monitored.
        </p>

        <div className={styles.formGroup} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="familySearch">Link by Username:</label>
            <input
              type="text"
              id="familySearch"
              value={familySearch}
              onChange={(e) => setFamilySearch(e.target.value)}
              placeholder="Enter exact username"
              onKeyDown={(e) => e.key === 'Enter' && sendFamilyLink()}
            />
          </div>
          <button className={styles.updateButton} onClick={sendFamilyLink} disabled={familyLoading} style={{ whiteSpace: 'nowrap' }}>
            {familyLoading ? 'Sending...' : 'Send Request'}
          </button>
        </div>

        {familyLinks.length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {familyLinks.map((link: any) => {
              const isTarget = link.targetId === user?.id;
              const otherUser = isTarget ? link.requester : link.target;
              const isPending = link.status === 'pending';
              const isConfirmed = link.status === 'confirmed';

              return (
                <div key={link.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: '6px',
                  background: 'rgba(var(--color-gold-rgb, 194,168,118), 0.08)',
                  border: `1px solid ${isConfirmed ? 'rgba(34,197,94,0.3)' : 'rgba(var(--color-gold-rgb, 194,168,118), 0.2)'}`,
                }}>
                  <div>
                    <strong>{otherUser?.displayName || otherUser?.username}</strong>
                    <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '0.85rem' }}>
                      {isPending ? (isTarget ? '(awaiting your response)' : '(pending)') : `(${link.status})`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {isPending && isTarget && (
                      <>
                        <button className={styles.updateButton} onClick={() => respondFamilyLink(link.id, 'accept')} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                          <FontAwesomeIcon icon={faCheck} /> Accept
                        </button>
                        <button className={styles.updateButton} onClick={() => respondFamilyLink(link.id, 'reject')} style={{ padding: '4px 10px', fontSize: '0.8rem', opacity: 0.7 }}>
                          <FontAwesomeIcon icon={faTimes} /> Reject
                        </button>
                      </>
                    )}
                    {(isConfirmed || (isPending && !isTarget)) && (
                      <button className={styles.updateButton} onClick={() => removeFamilyLink(link.id)} style={{ padding: '4px 10px', fontSize: '0.8rem', opacity: 0.7 }}>
                        <FontAwesomeIcon icon={faTrash} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
